import React, { createContext, useContext, useEffect, useReducer } from 'react'
import { RuleGroupType } from 'react-querybuilder'
import { ReportBuilderModel, MODEL_CONFIG } from './fields'
import type { CustomField } from './fields'
import type { MongoQuery } from './index'
import { useReportBuilderReportById } from 'src/queries/cat/reporting/reportbuilder/useReportBuilderReportById'
import { reducer, initialState } from './reducer'

interface ReportBuilderContextValue {
  fields: CustomField[]
  requiredFields: string[]
  model: ReportBuilderModel
  setModel: (m: ReportBuilderModel) => void
  query: RuleGroupType | undefined
  setQuery: (q: RuleGroupType) => void
  selectedFields: CustomField[]
  toggleField: (name: string) => void
  mongoQuery: MongoQuery | undefined
  runQuery: () => void
  isDirty: boolean
  isNew: boolean
  id: string
  title: string | undefined
}

const ReportBuilderContext = createContext<ReportBuilderContextValue | undefined>(undefined)

export const ReportBuilderProvider: React.FC<{ children: React.ReactNode, id: string }> = ({ children, id }) => {
  const isNew = id === 'new'
  const savedReport = useReportBuilderReportById(id, { enabled: !isNew })
  const [state, dispatch] = useReducer(reducer, initialState)

  useEffect(() => {
    if (savedReport === undefined) return
    const model: ReportBuilderModel = (savedReport.model as ReportBuilderModel | null | undefined) ?? 'position'
    const allFields = MODEL_CONFIG[model].fields
    const loadedFields = allFields.filter(f => savedReport.fields?.includes(f.name) ?? false)
    dispatch({
      type: 'LOAD_REPORT',
      model,
      query: savedReport.query as RuleGroupType,
      selectedFields: loadedFields.length > 0 ? loadedFields : MODEL_CONFIG[model].requiredFieldObjects
    })
  }, [savedReport?.model, savedReport?.query, savedReport?.fields])

  const isDirty = isNew
    ? state.query !== undefined
    : state.model !== ((savedReport?.model as ReportBuilderModel | null | undefined) ?? 'position') ||
      JSON.stringify(state.query) !== JSON.stringify(savedReport?.query) ||
      JSON.stringify([...state.selectedFields.map(f => f.name)].sort((a, b) => a.localeCompare(b))) !==
      JSON.stringify([...(savedReport?.fields ?? [])].filter((f): f is string => f != null).sort((a, b) => a.localeCompare(b)))

  return (
    <ReportBuilderContext.Provider value={{
      fields: MODEL_CONFIG[state.model].fields,
      requiredFields: MODEL_CONFIG[state.model].requiredFields,
      model: state.model,
      setModel: (m) => dispatch({ type: 'SET_MODEL', model: m }),
      query: state.query,
      setQuery: (q) => dispatch({ type: 'SET_QUERY', query: q }),
      selectedFields: state.selectedFields,
      toggleField: (name) => dispatch({ type: 'TOGGLE_FIELD', name }),
      mongoQuery: state.mongoQuery,
      runQuery: () => dispatch({ type: 'RUN_QUERY' }),
      isDirty,
      isNew,
      id,
      title: savedReport?.title ?? undefined
    }}
    >
      {children}
    </ReportBuilderContext.Provider>
  )
}

export const useReportBuilderContext = (): ReportBuilderContextValue => {
  const ctx = useContext(ReportBuilderContext)
  if (ctx === undefined) throw new Error('useReportBuilderContext must be used within ReportBuilderProvider')
  return ctx
}
